/**
 * Uplifted verbatim from `apps/web/src/utils/requestManager/index.ts`.
 *
 * Moved WHOLE rather than seeded, unusually — the file is already exactly what core wants:
 * zero imports, no Redux, no React, no Axios, no DOM. Its own header says so and the
 * measurement agrees. Web's `startGlobalLoader`/`stopGlobalLoader` are one-line wrappers
 * over `increment()`/`decrement()`, which is why the loader needs NO injected seam.
 *
 * ⚠️ Its two SIBLINGS deliberately stay in apps/web, because they are the coupled halves:
 *   - `globalLoaderReduxSync.ts` imports the store + `globalLoaderSlice`
 *   - `globalInteractionLock.ts` writes `document.body.style.pointerEvents` (DOM)
 * Both merely SUBSCRIBE to this counter, so splitting here costs nothing.
 *
 * Mobile has no requestManager at all; it drives its loader through `GlobalSlice.setLoader`.
 * So this is a one-side module today — a FOLDER with one file, per the ledger's
 * "ALWAYS A FOLDER" rule, ready for an `index.native.ts` if mobile ever adopts it.
 */

/**
 * Listener signature.
 * Called whenever active request count changes.
 */
type Listener = (count: number) => void;

class RequestManager {
  /**
   * Number of currently active async operations.
   */
  private activeRequests = 0;

  /**
   * Set of subscribed listeners.
   * Using Set ensures:
   * - No duplicate listeners
   * - O(1) add/remove
   */
  private listeners = new Set<Listener>();

  /**
   * Increment active request count.
   *
   * Call this when:
   * - An API request starts
   * - A manual async task begins
   *
   * Triggers notification to subscribers.
   */
  increment(): void {
    this.activeRequests += 1;
    this.notify();
  }

  /**
   * Decrement active request count.
   *
   * Call this when:
   * - An API request finishes
   * - An async task resolves or fails
   *
   * Safety:
   * - Prevents count from going below 0.
   */
  decrement(): void {
    if (this.activeRequests === 0) return;

    this.activeRequests -= 1;
    this.notify();
  }

  /**
   * Get current active request count.
   *
   * Useful for:
   * - Immediate synchronous checks
   * - Race-condition guards
   */
  getCount(): number {
    return this.activeRequests;
  }

  /**
   * Subscribe to request count changes.
   *
   * Returns an unsubscribe function.
   *
   * Example:
   * const unsubscribe = requestManager.subscribe(...)
   * unsubscribe()
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners about current active request count.
   *
   * Called internally whenever count changes.
   */
  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.activeRequests);
    }
  }
}

/**
 * Singleton instance.
 *
 * Exported as a shared global request tracker.
 * Should be used across:
 * - Axios interceptors
 * - Manual async flows
 * - Loader synchronization modules
 */
export const requestManager = new RequestManager();
